import { Download } from '@mui/icons-material';
import { Avatar, Card, List, ListItem, Typography, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { DateTime } from 'luxon';
import { type FC, useState } from 'react';
import { AvaniDatePicker } from '../Buttons';
import { AvaniExport } from '../Utils';

interface AvaniExportCardProps {
  visitId: string;
  sx?: any;
  markedDates?: Date[];
  setDialogOpen: (val: boolean) => void;
}

const AvaniExportStyledCard = styled(Card)(
  ({ theme }) => `
  border-radius: 16px;
  padding: 0;
  width: auto;
  flex: 1;

  .MuiTypography-root.MuiTypography-h3 {
    margin: 0;
    line-height: 1;
    padding-bottom: 2px;
    display: block;
    width: 100%;
    float: left;
  }
`
);

export const AvaniExportCard: FC<AvaniExportCardProps> = ({ markedDates, visitId, sx, setDialogOpen }) => {
  const [fromDate, setFromDate] = useState<DateTime | null>(DateTime.now());
  const [toDate, setToDate] = useState<DateTime | null>(DateTime.now());
  const theme = useTheme();
  const [exportStarted, setExportStarted] = useState(false);

  return (
    <AvaniExportStyledCard
      raised={true}
      sx={{
        ...sx,
        height: '100%',
        minHeight: useMediaQuery(theme.breakpoints.down('sm')) ? '208px' : '140px',
        backgroundColor: theme.colors.avani.lightTurc
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'row', margin: '16px', height: 'calc(100% - 32px)' }}>
        <div style={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <Typography variant={'h3'} color={theme.colors.avani.teal} sx={{ display: 'block' }}>
            Export
          </Typography>
          <List disablePadding sx={{ float: 'left', paddingTop: '12px' }}>
            <ListItem
              disablePadding
              sx={{ display: 'inline-block', width: 'auto', marginRight: '20px', float: 'left' }}
            >
              <Typography variant={'body2'} sx={{ display: 'block', marginRight: 0, paddingTop: 0 }}>
                From
              </Typography>
              <AvaniDatePicker
                onSelectDate={(date: DateTime | null) =>
                  setFromDate(date?.setZone('utc', { keepLocalTime: true }).startOf('day') ?? null)
                }
                markedDates={markedDates}
              />
            </ListItem>
            <ListItem disablePadding sx={{ display: 'inline-block', width: 'auto', float: 'left' }}>
              <Typography
                variant={'body2'}
                sx={{
                  display: 'block',
                  marginRight: 0,
                  marginLeft: 0,
                  mt: useMediaQuery(theme.breakpoints.down('sm')) ? 1 : 0
                }}
              >
                To
              </Typography>
              <AvaniDatePicker
                onSelectDate={(date: DateTime | null) =>
                  setToDate(date?.setZone('utc', { keepLocalTime: true }).endOf('day') ?? null)
                }
                markedDates={markedDates}
              />
            </ListItem>
          </List>
        </div>
        <Avatar
          sx={{
            backgroundColor: theme.colors.avani.teal,
            cursor: 'pointer',
            borderRadius: '8px',
            marginTop: '4px',
            marginRight: '4px'
          }}
          onClick={() => setExportStarted(true)}
        >
          <Download sx={{ fill: 'white' }} />
        </Avatar>
        {!!fromDate && !!toDate && exportStarted && (
          <AvaniExport
            visitId={visitId}
            startDate={fromDate}
            endDate={toDate}
            onComplete={() => {
              setExportStarted(false);
              setDialogOpen(false);
            }}
          />
        )}
      </div>
    </AvaniExportStyledCard>
  );
};
